import { useMutation } from '@apollo/client';
import { BrandIdKeys } from '@seek/brand-types';
import { useCallback } from 'react';
import { useSelector, shallowEqual } from 'react-redux';

import {
  EDIT_PAGE_CONNECTION_JOB_EDIT_ERROR,
  EDIT_PAGE_CONNECTION_JOB_EDIT_NO_REVISION_ID_ERROR,
} from 'src/app/shared/constants/errorMessages';
import { log } from 'src/app/utils/logging';
import EDIT_JOB from 'src/graphql/editJob';
import type {
  EditJobMutation,
  EditJobMutationVariables,
} from 'src/graphql/generated';
import type { AppState } from 'src/types/appState';

export const useEditJob = () => {
  const {
    seekAdvertiserId,
    advertiserNation,
    interactionId,
    draftId,
    sourceJobId,
    brandLegacyId,
    brandId,
  } = useSelector(
    (state: AppState) => ({
      seekAdvertiserId: state.session.seekAdvertiserId,
      advertiserNation: state.session.hirerAccount.nation,
      interactionId: state.session.interactionId,
      draftId: state.session.draftId,
      sourceJobId: state.session.sourceJobId,
      brandLegacyId: state.formData.brand?.legacyId,
      brandId: state.formData.brand?.[BrandIdKeys.id],
    }),
    shallowEqual,
  );

  const [editJobMutation] = useMutation<
    EditJobMutation,
    EditJobMutationVariables
  >(EDIT_JOB, {
    context: {
      headers: {
        site: advertiserNation,
        'interaction-id': interactionId,
      },
    },
  });

  const editJob = useCallback(
    async (idempotencyKey: string) => {
      const { data, errors } = await editJobMutation({
        variables: {
          input: {
            advertiserId: String(seekAdvertiserId),
            draftId: String(draftId),
            idempotencyKey: String(idempotencyKey),
            jobId: String(sourceJobId),
            branding:
              Boolean(brandLegacyId) && Boolean(brandId)
                ? {
                    brandId: String(brandId),
                    legacyId: Number(brandLegacyId),
                  }
                : undefined,
          },
        },
      });

      if (errors && errors.length > 0) {
        log.error('useEditJob | editJobMutation', {
          errors,
          seekAdvertiserId,
          advertiserNation,
          interactionId,
          draftId,
          sourceJobId,
          brandLegacyId,
          brandId,
        });
        throw new Error(EDIT_PAGE_CONNECTION_JOB_EDIT_ERROR);
      }

      if (!data?.editJob?.revisionId) {
        log.error('useEditJob | editJobMutation | no revisionId', {
          seekAdvertiserId,
          advertiserNation,
          interactionId,
          draftId,
          sourceJobId,
          brandLegacyId,
          brandId,
        });
        throw new Error(EDIT_PAGE_CONNECTION_JOB_EDIT_NO_REVISION_ID_ERROR);
      }

      return data.editJob.revisionId;
    },
    [
      advertiserNation,
      brandId,
      brandLegacyId,
      draftId,
      editJobMutation,
      interactionId,
      seekAdvertiserId,
      sourceJobId,
    ],
  );

  return editJob;
};
